import sdk from './sdk'
import { isTG } from './global'

/**
 * Telegram 原生返回键（BackButton）的唯一管理入口。
 *
 * 为什么要收敛到一处：
 * - sdk.BackButton 是全局单例，多个组件各自 show/hide + onClick 时，
 *   后挂载的组件卸载会把前一个组件还需要的按钮一起 hide 掉，事件也会重复触发。
 * - 全屏（Bot API 8.0）后 Telegram 自己的顶栏没了，右上角胶囊里只剩 ✕/⋯；
 *   只有调用 BackButton.show() 时 ✕ 才会变成「← 返回」，Android 系统返回键
 *   也只有这时才会派发成 back_button_pressed（否则直接关掉小程序）。
 *   所以「是否显示」必须由一个地方根据当前状态统一决定。
 *
 * 优先级：页面级 handler（useBackButton，如 MyNav / Back 组件、customBackHandler）
 * 永远优先于全局兜底（TgBackButton 组件按路由历史决定能否后退）。
 * 页面级有多个时取最后注册的（内层子视图覆盖外层）。
 */
type BackHandler = () => void

const pageHandlers: BackHandler[] = []
let fallbackHandler: BackHandler | null = null
let fallbackEnabled = false

// 点击分发只走 sdk.BackButton.onClick 这一条路，与 show()/hide() 共用同一版本门槛：
// 版本未就绪时 show() 同样不会生效，绕过 onClick 直接绑原始事件没有意义。
// 绑定句柄记在 window 上：dev HMR 重新求值本模块时旧的 dispatch 仍留在 sdk 的回调表里，
// 不先解绑会重复触发、一次退两级。版本未就绪时不落 key，下次 sync 重试。
const BOUND_KEY = '__ttTgBackDispatch'

function dispatch() {
  const usePage = pageHandlers.length > 0
  const handler = usePage ? pageHandlers[pageHandlers.length - 1] : (fallbackEnabled ? fallbackHandler : null)
  const which = usePage ? 'page' : (handler ? 'fallback' : 'none')
  process.env.NEXT_PUBLIC_RM_CONSOLE != 'true' && console.log('[TG] back_button_pressed → %s handler, page=%d fallback=%s', which, pageHandlers.length, fallbackEnabled)
  handler?.()
}

function bindDispatch() {
  const w = window as any
  const prev = w[BOUND_KEY]
  if (prev === dispatch) return
  if (!sdk.isVersionAtLeast('6.1')) return
  if (typeof prev === 'function') sdk.BackButton.offClick(prev)
  sdk.BackButton.onClick(dispatch)
  w[BOUND_KEY] = dispatch
}

function sync() {
  if (!isTG || typeof window === 'undefined') return
  bindDispatch()
  const visible = pageHandlers.length > 0 || (fallbackEnabled && !!fallbackHandler)
  if (visible) {
    sdk.BackButton.show()
  } else {
    sdk.BackButton.hide()
  }
}

/**
 * 注册页面级返回处理函数，返回注销函数。供 useBackButton 使用。
 */
export function pushPageBackHandler(handler: BackHandler): () => void {
  pageHandlers.push(handler)
  sync()
  return () => {
    const i = pageHandlers.lastIndexOf(handler)
    if (i >= 0) pageHandlers.splice(i, 1)
    sync()
  }
}

/**
 * 设置全局兜底：没有页面级 handler 时，按 enabled 决定是否显示返回键，点击走 handler。
 */
export function setFallbackBackHandler(enabled: boolean, handler: BackHandler | null) {
  fallbackEnabled = enabled
  fallbackHandler = handler
  sync()
}
